import type { GridStackWidget } from 'gridstack';
import { DashboardPosition } from '../types/dashboard.types';

export const GRID_COLUMNS = 12;

/** Row height of the dashboard grid, which must match the viewer's `auto-rows-[86px]` in dashboard-viewer.component.html. */
export const GRID_ROW_HEIGHT_PX = 86;

export const DEFAULT_TILE_WIDTH = 4;
export const DEFAULT_TILE_HEIGHT = 3;

export const MIN_CANVAS_ROWS = 20;

function toGridInt(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.trunc(value)
    : fallback;
}

export function clampPosition(
  position: Partial<DashboardPosition> | null | undefined
): DashboardPosition {
  const source = position ?? {};
  const w = Math.min(GRID_COLUMNS, Math.max(1, toGridInt(source.w, 1)));
  const h = Math.max(1, toGridInt(source.h, 1));
  const x = Math.min(GRID_COLUMNS - w, Math.max(0, toGridInt(source.x, 0)));
  const y = Math.max(0, toGridInt(source.y, 0));
  return { x, y, w, h };
}

export function normalizePositions(
  positions: readonly Partial<DashboardPosition>[] | null | undefined
): DashboardPosition[] {
  if (!Array.isArray(positions)) {
    return [];
  }
  return positions.map(clampPosition);
}

export function defaultPositionForNewTile(
  existing: readonly Partial<DashboardPosition>[] | null | undefined,
  size?: { w?: number; h?: number }
): DashboardPosition {
  const normalized = normalizePositions(existing);
  const nextRow = normalized.reduce(
    (max, pos) => Math.max(max, pos.y + pos.h),
    0
  );
  return clampPosition({
    x: 0,
    y: nextRow,
    w: size?.w ?? DEFAULT_TILE_WIDTH,
    h: size?.h ?? DEFAULT_TILE_HEIGHT,
  });
}

export function toGridWidget(
  position: Partial<DashboardPosition> | null | undefined,
  id?: string
): GridStackWidget {
  const { x, y, w, h } = clampPosition(position);
  return id === undefined ? { x, y, w, h } : { x, y, w, h, id };
}

export function fromGridNode(
  node: Pick<GridStackWidget, 'x' | 'y' | 'w' | 'h'> | null | undefined
): DashboardPosition {
  return clampPosition(node);
}
